import moment from "moment";
import callApi, {  BaseResponse, BaseResponseDto, BaseResponsePagedDto, callApiBlob, callApiFile } from "./baseRequest";
import { CustomerResponseDto } from "./customerService";

const route = 'call-center';

export interface Response {
    id: string,
    number: number,
    createdAt?: Date,
    customer?: CustomerResponseDto,
    sector?: ResponseSector,
    ticketLevel?: ResponseTicketLevel,
    telephone?: string,
    isLate?: boolean,
    status?: TicketStatusEnum
}

export interface NewTicket {
    description: string,
    telephone: string,    
    ticketLevelId: string
}

export interface AlterTicket {
    status: TicketStatusEnum,
    sectorId: string,
    emails?: string,
    respName?: string
}

export interface ResponseTicket {
    id: string,
    number: number,
    createdAt: Date,
    description: string,
    telephone: string,    
    status: TicketStatusEnum,
    customer?: CustomerResponseDto,
    sector?: ResponseSector,
    ticketLevel?: ResponseTicketLevel,
    files: ResponseTicketFile[],
    histories: ResponseTicketHistory[]
    user: ResponseTicketUser,
}

export interface ResponseSector {
    id: string,
    namePT: string,
    nameES: string,
    nameEN: string,
    emails: string[]
}

export interface ResponseTicketLevel {
    id: string,
    descriptionPT: string,
    descriptionES: string,
    descriptionEN: string
}

export interface ResponseTicketFile {
    id: string,
    urlBlob: string,
    name: string,
    isUnipar: boolean
}

export interface ResponseTicketUser {
    id: string,
    name: string,
    email: string
}

export interface ResponseTicketHistory {
    id: string,
    createdAt: Date,
    description: string,
    isUnipar: boolean,
    changeBy?: string
}

export interface RequestReportData {
    customers: Array<string>,
    sectors: Array<string>,
    levels: Array<string>,
    createdDateInitial: string,
    createdDateFinal: string,
    isPVC?: boolean | null,
    country?: string,
}

export interface ResponseReportData {
    totalTickets: number;
    totalStatus: ITicketTotalStatusDto[];
}

export enum TicketStatusEnum {
    Pending = 1,
    Served = 2,
    Finished = 3
}

export interface ITicketTotalStatusDto {
    status: TicketStatusEnum;
    descriptionPT: string;
    descriptionES: string;
    descriptionEN: string;
    totalNotIsLate: number;
    totalIsLate: number;
    totalTickets: number;
    percentNotIsLate: number;
    percentIsLate: number;
    totalSectors: ITicketTotalSectorDto[];
    totalLevels: ITicketTotalLevelDto[];
}

interface ITicketTotalSectorDto {
    id: string;
    descriptionPT: string;
    descriptionES: string;
    descriptionEN: string;
    totalNotIsLate: number;
    totalIsLate: number;
    totalTickets: number;
    percentNotIsLate: number;
    percentIsLate: number;
}

interface ITicketTotalLevelDto {
    id: string;
    descriptionPT: string;
    descriptionES: string;
    descriptionEN: string;
    totalNotIsLate: number;
    totalIsLate: number;
    totalTickets: number;
    percentNotIsLate: number;
    percentIsLate: number;
}

export function getData(
    take: number,
    skip: number,
    issuer?: string,
    number?: string,
    sectorId?: string,
    ticketLevelId?: string,
    status?: string,
    sort?: string,
    language?: string,
    initialDate?: string,
    finalDate?: string,
    group?: string,
    country?: string,): Promise<BaseResponsePagedDto<Response[]>> {
    const result: BaseResponsePagedDto<Response[]> = {
        error: '',
        errorPT: '',
        errorES: '',
        errorEN: '',
        data: [],
        success: false,
        currentTake: take,
        totalItems: 0,
        totalPages: 0
    };

    let url = `${route}/list-tickets/${language}?issuer=${issuer}&take=${take}&skip=${skip}&number=${number}&status=${status}&sectorId=${sectorId}&ticketLevelId=${ticketLevelId}&sort=${sort}&initialDate=${initialDate}&finalDate=${finalDate}&country=${country}&ispvc=${group}`;

    const response = callApi(
        'GET',
        url
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
            result.data = resp?.data;
            result.totalItems = resp?.totalItems;
            result.totalPages = resp?.totalPages;
        }
        else {
            result.errorPT = resp.errorPT;
            result.errorEN = resp.errorEN;
            result.errorES = resp.errorES;
        }
        return result;
    })
    .catch(err => {
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    })

    return response;
};

export function getSectors(): Promise<BaseResponseDto<ResponseSector[]>> {
    const result: BaseResponseDto<ResponseSector[]> = {
        error: '',
        errorPT: '',
        errorES: '',
        errorEN: '',
        data: [],
        success: false
    };

    let url = `${route}/list-sectors`;

    const response = callApi(
        'GET',
        url
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
            result.data = resp?.data;
        }
        else {
            result.errorPT = resp.errorPT;
            result.errorEN = resp.errorEN;
            result.errorES = resp.errorES;
        }
        return result;
    })
    .catch(err => {
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    })

    return response;
};

export function getTicketLevels(): Promise<BaseResponseDto<ResponseTicketLevel[]>> {
    const result: BaseResponseDto<ResponseTicketLevel[]> = {
        error: '',
        errorPT: '',
        errorES: '',
        errorEN: '',
        data: [],
        success: false
    };

    let url = `${route}/list-ticket-levels`;

    const response = callApi(
        'GET',
        url
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
            result.data = resp?.data;
        }
        else {
            result.errorPT = resp.errorPT;
            result.errorEN = resp.errorEN;
            result.errorES = resp.errorES;
        }
        return result;
    })
    .catch(err => {
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    })

    return response;
};

export function getTicket(ticketId: string): Promise<BaseResponseDto<ResponseTicket>> {
    const result: BaseResponseDto<ResponseTicket> = {
        error: '',
        errorPT: '',
        errorES: '',
        errorEN: '',
        data: {
            id: '',
            number: 0,
            createdAt: new Date(),
            description: '',
            telephone: '',
            sector: {
                id: '',
                nameEN: '',
                nameES: '',
                namePT: '',
                emails: []
            },
            status: TicketStatusEnum.Pending,
            ticketLevel: {
                id: '',
                descriptionEN: '',
                descriptionES: '',
                descriptionPT: ''
            },
            user:{
                email: '',
                id: '',
                name: '',
            },
            files: [],
            histories: []
        },
        success: false
    };

    let url = `${route}/ticket/${ticketId}`;

    const response = callApi(
        'GET',
        url
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
            result.data = resp?.data;
        }
        else {
            result.errorPT = resp.errorPT;
            result.errorEN = resp.errorEN;
            result.errorES = resp.errorES;
        }
        return result;
    })
    .catch(err => {
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    })

    return response;
};

export function createTicket(ticket: NewTicket, files: File[], issuer: string = '') : Promise<BaseResponseDto<ResponseTicket>>
{    
    const result: BaseResponseDto<ResponseTicket> = {
        error: '',
        errorEN:'',
        errorES:'',
        errorPT:'',
        data: {
            id: '',
            createdAt: new Date(),
            description: '',
            number: 0,
            status: TicketStatusEnum.Pending,
            telephone: '',
            sector: undefined,
            ticketLevel: undefined,
            files: [],
            histories: [],
            user:{
                email: '',
                id: '',
                name: '',
            },
        },
        success: false,
        errors: []
    };

    const response = callApiFile(
        'POST', 
        `${route}/create-ticket/${issuer}`,
        files,
        ticket
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
            result.data = resp?.data;
        }
        else {
            result.errors = resp?.data;
            result.error = resp?.error;
            result.errorPT = resp?.errorPT;
            result.errorEN = resp?.errorEN;
            result.errorES = resp?.errorES;
            result.data = resp?.data;
        }
        return result;
    })
    .catch(err => {
        result.error = err.error;
        result.data = err?.data;
        result.errors = err?.data;
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    });

    return response;
};

export function addTicketFile(ticketId: string, files: File[]) : Promise<BaseResponse>
{    
    const result: BaseResponse = {
        error: '',
        errorEN:'',
        errorES:'',
        errorPT:'',
        success: false
    };

    const response = callApiFile(
        'POST', 
        `${route}/add-ticket-file/${ticketId}`,
        files
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
        }
        else {
            result.error = resp?.error;
            result.errorPT = resp?.errorPT;
            result.errorEN = resp?.errorEN;
            result.errorES = resp?.errorES;
        }
        return result;
    })
    .catch(err => {
        result.error = err.error;
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    });

    return response;
};

export function removeTicketFile(ticketFileId: string) : Promise<BaseResponse>
{    
    const result: BaseResponse = {
        error: '',
        errorEN:'',
        errorES:'',
        errorPT:'',
        success: false
    };

    const response = callApi(
        'DELETE', 
        `${route}/remove-ticket-file/${ticketFileId}`
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
        }
        else {
            result.error = resp?.error;
            result.errorPT = resp?.errorPT;
            result.errorEN = resp?.errorEN;
            result.errorES = resp?.errorES;
        }
        return result;
    })
    .catch(err => {
        result.error = err.error;
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    });

    return response;
};

export function createTicketReply(ticketId: string, message: string, respName?: string) : Promise<BaseResponseDto<ResponseTicketHistory>>
{    
    const result: BaseResponseDto<ResponseTicketHistory> = {
        error: '',
        errorEN:'',
        errorES:'',
        errorPT:'',
        data: {
            id: '',
            createdAt: new Date(),
            description: '',
            isUnipar: false
        },
        success: false,
        errors: []
    };

    const response = callApiFile(
        'POST', 
        `${route}/create-reply-ticket/${ticketId}`,
        [],
        {description: message, respName: respName}
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
            result.data = resp?.data;
        }
        else {
            result.errors = resp?.data;
            result.error = resp?.error;
            result.errorPT = resp?.errorPT;
            result.errorEN = resp?.errorEN;
            result.errorES = resp?.errorES;
            result.data = resp?.data;
        }
        return result;
    })
    .catch(err => {
        result.error = err.error;
        result.data = err?.data;
        result.errors = err?.data;
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    });

    return response;
};

export function alterTicket(ticketId: string, alterTicket: AlterTicket, language: string) : Promise<BaseResponse>
{    
    const result: BaseResponse = {
        error: '',
        errorEN:'',
        errorES:'',
        errorPT:'',
        success: false
    };

    const response = callApiFile(
        'PUT', 
        `${route}/alter-ticket/${language}/${ticketId}`,
        [],
        alterTicket
    )
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
        }
        else {
            result.error = resp?.error;
            result.errorPT = resp?.errorPT;
            result.errorEN = resp?.errorEN;
            result.errorES = resp?.errorES;
        }
        return result;
    })
    .catch(err => {
        result.error = err.error;
        result.errorPT = err.errorPT;
        result.errorEN = err.errorEN;
        result.errorES = err.errorES;

        return result;
    });

    return response;
};

export function GetReportData(body: RequestReportData): Promise<BaseResponseDto<ResponseReportData>> {
    const result: BaseResponseDto<any> = {
        error: '',
        data: null,
        success: false
    };

    const response = callApi('POST', `${route}/ticket-report`, body)
    .then(resp => {
        if (resp?.error == null ?? false) {
            result.success = true;
            result.data = resp.data;
        }
        else {
            result.error = 'An error occurred while generating the report.';
        }
        return result;
    })
    .catch(err => {
        result.error = err;

        return result;
    })

    return response;
};

export function GetFileReport(body: RequestReportData, language: string) : Promise<boolean> {
    let url = `${route}/file-ticket-report/${language}`;
    
    const p = callApiBlob(
        'POST',
        url,
        body
    )
    .then((resp) => {
        const blob = new Blob([resp as BlobPart], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const element = document.createElement('a');
        element.href = URL.createObjectURL(blob);
        element.download = `${moment().format('yyyy-MM-DD-HH-mm')}.xlsx`;
        document.body.appendChild(element);
        element.click();

        return true;
    })
    .catch(err => {
        return false;
    })

    return Promise.resolve(p);
};